import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Award, ClipboardCheck, MessageSquare, CheckCircle, XCircle, Clock, Sparkles, ChevronDown, Scale, Star, ShoppingCart, X } from "lucide-react";

const EVAL_STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  final: "bg-blue-100 text-blue-800",
  awarded: "bg-emerald-100 text-emerald-800",
};

const DISPOSITION_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  modified: "bg-blue-100 text-blue-800",
  ignored: "bg-zinc-100 text-zinc-700",
};

export default function SupplierEvaluation() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("evaluations");
  const [evaluations, setEvaluations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Award reason state — keyed by evaluation id
  const [awardingId, setAwardingId] = useState(null);
  const [awardedRfqId, setAwardedRfqId] = useState(null);
  const [awardReason, setAwardReason] = useState("");

  // Negotiation quotation selector state
  const [rfqs, setRfqs] = useState([]);
  const [selectedRfqId, setSelectedRfqId] = useState("");
  const [selectedQuotationId, setSelectedQuotationId] = useState("");

  // Weighted scoring panel state
  const [weightEdits, setWeightEdits] = useState({});

  const loadEvaluations = () => {
    setLoading(true);
    api.get("/supplier-evaluations")
      .then((r) => setEvaluations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadSuggestions = () => {
    setLoading(true);
    api.get("/negotiation-suggestions")
      .then((r) => setSuggestions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (tab === "evaluations") loadEvaluations();
    else {
      loadSuggestions();
      loadRfqs();
    }
  }, [tab]);

  const finalize = async (id) => {
    setActing(id);
    try {
      await api.patch(`/supplier-evaluations/${id}`, { status: "final" });
      toast.success("Evaluation finalized");
      loadEvaluations();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(null);
    }
  };

  const awardContract = async (id, reason) => {
    if (!reason || !reason.trim()) {
      toast.error("Award reason is required");
      return;
    }
    setActing(id);
    try {
      const { data } = await api.post(`/supplier-evaluations/${id}/award`, { award_reason: reason.trim() });
      toast.success("Contract awarded");
      // The award is only half the job — offer the order it authorises, with its terms carried over.
      const rfqId = data?.rfq_id || evaluations.find((e) => e.id === id)?.rfq_id;
      if (rfqId) setAwardedRfqId(rfqId);
      setAwardingId(null);
      setAwardReason("");
      loadEvaluations();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(null);
    }
  };

  const setDisposition = async (id, disposition) => {
    setActing(id);
    try {
      await api.patch(`/negotiation-suggestions/${id}/disposition`, { disposition });
      toast.success(`Suggestion ${disposition}`);
      loadSuggestions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(null);
    }
  };

  const loadRfqs = () => {
    api.get("/rfqs")
      .then((r) => setRfqs(r.data))
      .catch(() => {});
  };

  const generateSuggestions = async () => {
    if (!selectedQuotationId) {
      toast.error("Please select a quotation first");
      return;
    }
    setGenerating(true);
    try {
      await api.post("/negotiation-suggestions/generate", { quotation_id: selectedQuotationId });
      toast.success("AI suggestions generated");
      loadSuggestions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const selectedRfq = rfqs.find((r) => r.id === selectedRfqId);
  const quotationsForRfq = selectedRfq?.quotations || [];

  // Weighted scoring helpers
  const getEvalCriteria = (ev) => {
    if (!ev.scores || typeof ev.scores !== "object") return [];
    return Object.keys(ev.scores);
  };

  const getWeightForCriterion = (evId, key, ev) => {
    if (weightEdits[evId] && weightEdits[evId][key] !== undefined) return weightEdits[evId][key];
    const val = ev.scores[key];
    if (typeof val === "object" && val.weight !== undefined) return val.weight;
    const criteria = getEvalCriteria(ev);
    return criteria.length > 0 ? Math.round(100 / criteria.length) : 0;
  };

  const setWeight = (evId, key, value) => {
    setWeightEdits((prev) => ({
      ...prev,
      [evId]: { ...(prev[evId] || {}), [key]: Math.max(0, Math.min(100, Number(value) || 0)) },
    }));
  };

  const weightsSum = (evId, ev) => {
    const criteria = getEvalCriteria(ev);
    return criteria.reduce((sum, key) => sum + getWeightForCriterion(evId, key, ev), 0);
  };

  const computeWeightedScore = (ev, evId) => {
    const criteria = getEvalCriteria(ev);
    if (criteria.length === 0) return 0;
    const totalWeight = weightsSum(evId, ev);
    if (totalWeight === 0) return 0;
    return criteria.reduce((sum, key) => {
      const val = ev.scores[key];
      const score = typeof val === "object" ? val.score : val;
      const w = getWeightForCriterion(evId, key, ev);
      return sum + ((score || 0) * w) / totalWeight;
    }, 0);
  };

  const getRfqEvaluations = (rfqId) => evaluations.filter((ev) => ev.rfq_id === rfqId);

  const rfqIds = [...new Set(evaluations.filter((ev) => ev.rfq_id).map((ev) => ev.rfq_id))];

  const renderScores = (scores) => {
    if (!scores || typeof scores !== "object") return null;
    return (
      <div className="mt-2 space-y-1">
        {Object.entries(scores).map(([key, val]) => {
          const score = typeof val === "object" ? val.score : val;
          const weight = typeof val === "object" ? val.weight : null;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-400 w-36 truncate">{key.replace(/_/g, " ")}</span>
              <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-800 rounded-full" style={{ width: `${Math.min(score || 0, 100)}%` }} />
              </div>
              <span className="text-[10px] text-zinc-500 w-12 text-right">{(score || 0).toFixed(0)}{weight ? ` (${weight}%)` : ""}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      {awardedRfqId && (
        <div className="mb-4 flex items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3">
          <ShoppingCart size={16} className="text-emerald-700 shrink-0" />
          <p className="text-sm text-emerald-900 flex-1">
            Contract awarded. Raise the purchase order from this award so the order carries the
            quoted price, supplier and terms.
          </p>
          <button
            onClick={async () => {
              try {
                const { data } = await api.post(`/rfqs/${awardedRfqId}/create-purchase-order`);
                toast.success(`Purchase order ${data.po_number} created`);
                setAwardedRfqId(null);
                navigate(`/purchase-orders/${data.id}`);
              } catch (e) { toast.error(apiError(e)); }
            }}
            className="text-xs font-medium px-3 py-1.5 rounded-md bg-emerald-700 text-white hover:bg-emerald-800 shrink-0"
          >
            Create Purchase Order
          </button>
          <button onClick={() => setAwardedRfqId(null)} aria-label="Dismiss" className="text-emerald-700/60 hover:text-emerald-900 shrink-0">
            <X size={15} />
          </button>
        </div>
      )}
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
        <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Supplier Evaluation & Negotiation</h1>
        <p className="text-zinc-500 mt-2 text-sm">Evaluate suppliers, finalize scores, award contracts and manage negotiation suggestions.</p>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        <button
          onClick={() => setTab("evaluations")}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            tab === "evaluations" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"
          }`}>
          <ClipboardCheck size={14} className="inline mr-1.5 -mt-0.5" /> Evaluations
        </button>
        <button
          onClick={() => setTab("negotiations")}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            tab === "negotiations" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"
          }`}>
          <MessageSquare size={14} className="inline mr-1.5 -mt-0.5" /> Negotiations
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "evaluations" ? (
        /* ── Evaluations Tab ── */
        evaluations.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <ClipboardCheck size={26} className="mx-auto mb-3" />
            <p className="text-sm">No evaluations yet.</p>
          </div>
        ) : (
          <>
          <div className="mt-6 space-y-4">
            {evaluations.map((ev) => (
              <div key={ev.id} className="border border-zinc-200 bg-white rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium">{ev.supplier_name || `Supplier ${ev.supplier_id.slice(0, 8)}`}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${EVAL_STATUS_STYLE[ev.status] || EVAL_STATUS_STYLE.draft}`}>
                        {ev.status}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">ID: {ev.id.slice(0, 8)}... {ev.rfq_id ? `| RFQ: ${ev.rfq_id.slice(0, 8)}...` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Overall</p>
                      <p className="text-2xl font-heading font-bold">{(ev.overall_score || 0).toFixed(1)}</p>
                    </div>
                  </div>
                </div>

                {renderScores(ev.scores)}

                {ev.notes && <p className="mt-3 text-xs text-zinc-500 italic">{ev.notes}</p>}

                <div className="mt-4 flex items-center gap-2">
                  {ev.status === "draft" && (
                    <button
                      onClick={() => finalize(ev.id)}
                      disabled={acting === ev.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-800 rounded-md text-xs font-medium hover:bg-blue-100 disabled:opacity-40">
                      <CheckCircle size={12} /> {acting === ev.id ? "Finalizing..." : "Finalize"}
                    </button>
                  )}
                  {ev.status === "final" && awardingId !== ev.id && (
                    <button
                      onClick={() => { setAwardingId(ev.id); setAwardReason(""); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100">
                      <Award size={12} /> Award Contract
                    </button>
                  )}
                  {ev.status === "final" && awardingId === ev.id && (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        placeholder="Award reason (required)"
                        value={awardReason}
                        onChange={(e) => setAwardReason(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-zinc-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <button
                        onClick={() => awardContract(ev.id, awardReason)}
                        disabled={acting === ev.id || !awardReason.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 disabled:opacity-40">
                        <Award size={12} /> {acting === ev.id ? "Awarding..." : "Confirm Award"}
                      </button>
                      <button
                        onClick={() => { setAwardingId(null); setAwardReason(""); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-zinc-600 rounded-md text-xs font-medium hover:bg-zinc-200">
                        Cancel
                      </button>
                    </div>
                  )}
                  {ev.status === "awarded" && (
                    <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                      <Award size={10} /> Contract Awarded
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Weighted Scoring Panel ── */}
          {rfqIds.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center gap-2 mb-4">
                <Scale size={16} className="text-zinc-500" />
                <h2 className="text-sm font-semibold text-zinc-800">Weighted Score Ranking</h2>
              </div>
              {rfqIds.map((rfqId) => {
                const rfqEvals = getRfqEvaluations(rfqId);
                if (rfqEvals.length === 0) return null;
                const criteria = getEvalCriteria(rfqEvals[0]);
                if (criteria.length === 0) return null;

                const ranked = rfqEvals
                  .map((ev) => ({ ...ev, weightedScore: computeWeightedScore(ev, ev.id) }))
                  .sort((a, b) => b.weightedScore - a.weightedScore);
                const topId = ranked[0]?.id;
                const totalWeight = weightsSum(rfqEvals[0].id, rfqEvals[0]);
                const weightValid = totalWeight === 100;

                return (
                  <div key={rfqId} className="border border-zinc-200 bg-white rounded-lg p-6 mb-4">
                    <p className="text-xs text-zinc-400 mb-4">RFQ: {rfqId.slice(0, 12)}...</p>

                    {/* Weight controls */}
                    <div className="mb-5">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-2">Criterion Weights</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {criteria.map((key) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-[10px] text-zinc-500 truncate flex-1">{key.replace(/_/g, " ")}</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={getWeightForCriterion(rfqEvals[0].id, key, rfqEvals[0])}
                                onChange={(e) => {
                                  rfqEvals.forEach((ev) => setWeight(ev.id, key, e.target.value));
                                }}
                                className="w-14 px-1.5 py-1 border border-zinc-300 rounded text-[10px] text-center focus:outline-none focus:ring-1 focus:ring-zinc-500"
                              />
                              <span className="text-[10px] text-zinc-400">%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`text-[10px] font-medium ${weightValid ? "text-emerald-600" : "text-red-500"}`}>
                          Total: {totalWeight}%
                        </span>
                        {!weightValid && (
                          <span className="text-[10px] text-red-400">Weights must sum to 100%</span>
                        )}
                      </div>
                    </div>

                    {/* Ranking table */}
                    <div className="border border-zinc-100 rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-zinc-50 text-zinc-400">
                            <th className="text-left px-3 py-2 font-medium text-[10px] uppercase tracking-[0.1em]">Rank</th>
                            <th className="text-left px-3 py-2 font-medium text-[10px] uppercase tracking-[0.1em]">Supplier</th>
                            {criteria.map((key) => (
                              <th key={key} className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.1em]">{key.replace(/_/g, " ")}</th>
                            ))}
                            <th className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.1em]">Weighted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ranked.map((ev, idx) => {
                            const isTop = ev.id === topId;
                            return (
                              <tr key={ev.id} className={`border-t border-zinc-100 ${isTop ? "bg-emerald-50" : ""}`}>
                                <td className="px-3 py-2.5 flex items-center gap-1.5">
                                  {isTop && <Star size={10} className="text-emerald-600" />}
                                  <span className={`font-medium ${isTop ? "text-emerald-700" : "text-zinc-600"}`}>{idx + 1}</span>
                                </td>
                                <td className={`px-3 py-2.5 font-medium ${isTop ? "text-emerald-800" : "text-zinc-800"}`}>
                                  {ev.supplier_name || `Supplier ${ev.supplier_id.slice(0, 8)}`}
                                </td>
                                {criteria.map((key) => {
                                  const val = ev.scores[key];
                                  const score = typeof val === "object" ? val.score : val;
                                  return (
                                    <td key={key} className="text-right px-3 py-2.5 text-zinc-600">{(score || 0).toFixed(1)}</td>
                                  );
                                })}
                                <td className={`text-right px-3 py-2.5 font-bold ${isTop ? "text-emerald-700" : "text-zinc-800"}`}>
                                  {ev.weightedScore.toFixed(1)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
        )
      ) : (
        /* ── Negotiations Tab ── */
        <>
          <div className="mt-6 flex items-end gap-3">
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">RFQ</label>
                <div className="relative">
                  <select
                    value={selectedRfqId}
                    onChange={(e) => { setSelectedRfqId(e.target.value); setSelectedQuotationId(""); }}
                    className="w-full appearance-none px-3 py-2 pr-8 border border-zinc-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-zinc-500 focus:border-zinc-500">
                    <option value="">Select RFQ...</option>
                    {rfqs.map((r) => (
                      <option key={r.id} value={r.id}>{r.title || r.id.slice(0, 12)}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Quotation</label>
                <div className="relative">
                  <select
                    value={selectedQuotationId}
                    onChange={(e) => setSelectedQuotationId(e.target.value)}
                    disabled={!selectedRfqId}
                    className="w-full appearance-none px-3 py-2 pr-8 border border-zinc-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-zinc-500 focus:border-zinc-500 disabled:bg-zinc-50 disabled:text-zinc-400">
                    <option value="">{selectedRfqId ? "Select quotation..." : "Select an RFQ first"}</option>
                    {quotationsForRfq.map((q) => (
                      <option key={q.id} value={q.id}>{q.supplier_name || q.id.slice(0, 12)}{q.total_price != null ? ` — ${q.total_price.toFixed(2)}` : ""}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <button
              onClick={generateSuggestions}
              disabled={generating || !selectedQuotationId}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-950 text-white rounded-md text-xs font-medium hover:bg-zinc-800 disabled:opacity-40 whitespace-nowrap">
              <Sparkles size={14} /> {generating ? "Generating..." : "Generate AI Suggestions"}
            </button>
          </div>
        {suggestions.length === 0 ? (
          <div className="mt-6 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <MessageSquare size={26} className="mx-auto mb-3" />
            <p className="text-sm">No negotiation suggestions yet. Click Generate to create AI-powered suggestions.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {suggestions.map((s) => (
              <div key={s.id} className="border border-zinc-200 bg-white rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${DISPOSITION_STYLE[s.disposition] || DISPOSITION_STYLE.pending}`}>
                        {s.disposition}
                      </span>
                      {s.counter_offer_price != null && (
                        <span className="text-xs text-zinc-500">Counter: <span className="font-medium text-zinc-800">{s.counter_offer_price.toFixed(2)}</span></span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-800">{s.suggestion_text}</p>
                    {s.reasoning && (
                      <p className="mt-2 text-xs text-zinc-500">{s.reasoning}</p>
                    )}
                    <p className="text-[10px] text-zinc-300 mt-2">Quotation: {s.quotation_id.slice(0, 8)}...</p>
                  </div>
                </div>

                {s.disposition === "pending" && (
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={() => setDisposition(s.id, "accepted")}
                      disabled={acting === s.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100 disabled:opacity-40">
                      <CheckCircle size={12} /> Accept
                    </button>
                    <button
                      onClick={() => setDisposition(s.id, "modified")}
                      disabled={acting === s.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 disabled:opacity-40">
                      <XCircle size={12} /> Modify
                    </button>
                    <button
                      onClick={() => setDisposition(s.id, "ignored")}
                      disabled={acting === s.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-md text-xs font-medium hover:bg-zinc-200 disabled:opacity-40">
                      <Clock size={12} /> Ignore
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
      )}
    </div>
  );
}
